import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addMonths, subMonths, isBefore, isAfter } from 'date-fns'
import MonthCalendar from './MonthCalendar'
import moment from 'moment'
import { DateRangeType } from '../_config/types'
import Button from '../elements/button'
import { IoCalendarOutline } from 'react-icons/io5'
import { MdOutlineRefresh } from 'react-icons/md'
import Icon from './icons'
import { useOutsideClick } from '../_common/hook/useOutsideClick'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'

type Value = DateRangeType | null

type CustomDatePickerProps = {
    type?: 'range' | null
    value?: Value
    onChange?: (value: Value) => void
    minDate?: Date
    maxDate?: Date
    format?: string
    clearIcon?: React.ReactNode
    calendarIcon?: React.ReactNode
    error?: string
    disabled?: boolean
    size?: 'md' | 'sm'
    resetStartDate?: Date
    resetEndDate?: Date
}

const RANGE_SIZE_CONFIG = {
    md: {
        container: 'h-[1.709rem] min-w-[13.150rem] max-w-[13.150rem]',
        text: 'text-[0.667rem]',
        btnWidth: 'w-[1.2rem]',
        iconSize: '1rem',
        calendarWidth: 'w-[24rem]',
        navBtn: 'w-[1.2rem] h-[1.2rem] text-[0.75rem]',
        navBtnHover: 'hover:text-[0.8rem]',
    },
    sm: {
        container: 'h-[1.2rem] min-w-[8.2rem] max-w-[8.2rem]',
        text: 'text-[0.438rem]',
        btnWidth: 'w-[1.2rem]',
        iconSize: '0.65rem',
        calendarWidth: 'w-[24rem]',
        navBtn: 'w-[1.2rem] h-[1.2rem] text-[0.75rem]',
        navBtnHover: 'hover:text-[0.8rem]',
    },
}

const DateRangePicker: React.FC<CustomDatePickerProps> = ({ disabled = false, type = null, value, onChange, minDate, maxDate, format = 'MM-dd-y', clearIcon = null, calendarIcon = null, error, size = 'md', resetStartDate, resetEndDate }) => {
    const { t } = useTranslation()
    const [currentMonth, setCurrentMonth] = useState<Date>(minDate || new Date())
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)
    const [showCalendar, setShowCalendar] = useState(false)

    // Update current month when minDate changes
    useEffect(() => {
        if (minDate) {
            setCurrentMonth(minDate)
        }
    }, [minDate])

    const [calendarPosition, setCalendarPosition] = useState<{
        top?: string
        bottom?: string
        left?: string
        right?: string
        transform?: string
        maxHeight?: string
    }>({ top: '100%', left: '0' })
    const containerRef = React.useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (showCalendar) {
            setStartDate(value ? value[0] : null)
            setEndDate(value ? value[1] : null)
        }
    }, [showCalendar, value])

    useEffect(() => {
        setStartDate(value ? value[0] : null)
        setEndDate(value ? value[1] : null)
    }, [value])

    /* useEffect(() => {
        if (onChange) {
            if (startDate && endDate) {
                onChange([startDate, endDate])
            }
        }
    }, [startDate, endDate]) -- back up on change impact code */

    const applyDates = () => {
        if (onChange) {
            if (startDate && endDate) {
                onChange([startDate, endDate])
            }
        }
        setShowCalendar(false)
    }

    const handleDateSelect = (date: Date) => {
        // Check if date is within allowed range
        if ((minDate && isBefore(date, minDate)) || (maxDate && isAfter(date, maxDate))) {
            return
        }

        if (!startDate || (startDate && endDate)) {
            setStartDate(date)
            setEndDate(null)
        } else if (isBefore(date, startDate)) {
            setStartDate(date)
        } else if (isAfter(date, startDate)) {
            // Ensure end date doesn't exceed maxDate
            if (maxDate && isAfter(date, maxDate)) {
                setEndDate(maxDate)
            } else {
                setEndDate(date)
            }
        } else {
            setEndDate(null)
        }
    }

    const handleClear = () => {
        if (resetStartDate && resetEndDate) {
            setStartDate(resetStartDate)
            setEndDate(resetEndDate)
            setShowCalendar(false)
            onChange?.([resetStartDate, resetEndDate])
        } else {
            setStartDate(null)
            setEndDate(null)
            setShowCalendar(false)
            onChange?.(null)
        }
    }

    const calculatePosition = () => {
        if (!containerRef.current) return

        const containerRect = containerRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const calendarWidth = 720 // 45rem
        const calendarHeight = 420 // Approximate height of calendar
        const margin = 16 // Margin from edges (8px * 2 for both sides)

        // Check available space in all directions
        const spaceOnRight = viewportWidth - containerRect.right
        const spaceOnLeft = containerRect.left
        const spaceBelow = viewportHeight - containerRect.bottom
        const spaceAbove = containerRect.top

        // Calculate position
        const newPosition: {
            top?: string
            bottom?: string
            left?: string
            right?: string
            transform?: string
            maxHeight?: string
        } = {}

        // Determine vertical position
        const hasSpaceBelow = spaceBelow >= calendarHeight + margin
        const hasSpaceAbove = spaceAbove >= calendarHeight + margin
        const shouldPositionBelow = hasSpaceBelow || (!hasSpaceAbove && spaceBelow >= spaceAbove)

        if (shouldPositionBelow) {
            newPosition.top = '100%'
            if (!hasSpaceBelow) {
                newPosition.maxHeight = `${spaceBelow - margin}px`
            }
        } else {
            newPosition.bottom = '100%'
            if (!hasSpaceAbove) {
                newPosition.maxHeight = `${spaceAbove - margin}px`
            }
        }

        // Horizontal positioning
        if (spaceOnRight >= calendarWidth + margin) {
            // Enough space on the right
            newPosition.left = '0'
            newPosition.right = 'auto'
        } else if (spaceOnLeft >= calendarWidth + margin) {
            // Enough space on the left
            newPosition.right = '0'
            newPosition.left = 'auto'
        } else {
            // Center it if there's not enough space on either side
            newPosition.left = '50%'
            newPosition.transform = 'translateX(-50%)'
        }

        setCalendarPosition(newPosition)
    }

    const handleCalendarToggle = (event: React.MouseEvent) => {
        if (disabled) return
        setShowCalendar(!showCalendar)
        if (!showCalendar) {
            // Calculate position when opening
            setTimeout(calculatePosition, 0)
        }
    }

    // Recalculate position on window resize
    useEffect(() => {
        if (showCalendar) {
            const handleResize = () => calculatePosition()
            window.addEventListener('resize', handleResize)
            return () => window.removeEventListener('resize', handleResize)
        }
    }, [showCalendar])

    const dateFormat: string = 'YYYY/MM/DD'

    // Display the applied value in the input; in-progress selection stays inside the calendar until Apply.
    const appliedStart = value?.[0] ?? null
    const appliedEnd = value?.[1] ?? null
    const formattedStart = appliedStart ? moment(appliedStart).format(dateFormat) : ''
    const formattedEnd = appliedEnd ? moment(appliedEnd).format(dateFormat) : ''

    useOutsideClick(containerRef, () => setShowCalendar(false))

    const sizeConfig = RANGE_SIZE_CONFIG[size]

    return (
        <div className="relative" ref={containerRef}>
            {/* Input Box */}
            <div className={`flex items-center border rounded-[0.267rem] shadow-sm ${disabled ? 'opacity-80 cursor-not-allowed bg-gray-200' : 'bg-white'}  ${sizeConfig.container}`}>
                <input
                    type="text"
                    readOnly
                    disabled={disabled}
                    value={appliedStart && appliedEnd ? `${formattedStart} ~ ${formattedEnd}` : ''}
                    placeholder={t(`${dateFormat} ~ ${dateFormat}`)}
                    className={`w-full ${sizeConfig.text} outline-none bg-transparent  pl-1.5 ${disabled ? 'text-gray-500 cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={handleCalendarToggle}
                />
                <button type="button" className={`text-slate-800 ${sizeConfig.btnWidth} h-full flex items-center justify-center ${disabled ? 'cursor-not-allowed opacity-50' : ''}`} onClick={handleCalendarToggle}>
                    <IoCalendarOutline size={sizeConfig.iconSize} />
                </button>
                <button disabled={disabled} type="button" className={`text-slate-800 ${sizeConfig.btnWidth} h-full bg-gray-200 flex items-center justify-center ${disabled ? 'cursor-not-allowed opacity-50' : ''}`} onClick={handleClear}>
                    <MdOutlineRefresh size={sizeConfig.iconSize} className="scale-x-[-1]" />
                </button>
            </div>

            {/* Calendar */}
            {showCalendar && (
                <div
                    className={`absolute bg-slate-900 pb-2 rounded-lg shadow-lg p-1 z-50 ${sizeConfig.calendarWidth} max-w-[calc(100vw-2rem)] overflow-auto ${calendarPosition.top ? 'mt-2' : 'mb-2'}`}
                    style={{
                        position: 'absolute',
                        ...calendarPosition,
                    }}
                >
                    <div className="flex relative">
                        <div className="flex items-center justify-between mb-4 absolute top-[-0.3rem] left-0 w-full py-4 px-4">
                            <button
                                type="button"
                                onClick={() => setCurrentMonth((prev) => subMonths(prev, 1))}
                                disabled={minDate && isBefore(subMonths(currentMonth, 1), minDate)}
                                className={`${sizeConfig.navBtn} rounded-md leading-[1.6rem] flex items-center text-white justify-center bg-slate-800 transition-all duration-200  ${minDate && isBefore(subMonths(currentMonth, 1), minDate) ? 'text-slate-700 opacity-50 cursor-not-allowed' : 'hover:bg-slate-500 ' + sizeConfig.navBtnHover}`}
                                aria-label={t('Previous Month')}
                            >
                                <IoIosArrowBack />
                            </button>
                            <button
                                type="button"
                                onClick={() => setCurrentMonth((prev) => addMonths(prev, 1))}
                                disabled={maxDate && isAfter(addMonths(currentMonth, 1), maxDate)}
                                className={`${sizeConfig.navBtn} rounded-md leading-none flex items-center text-white justify-center bg-slate-800 transition-all duration-200  ${maxDate && isAfter(addMonths(currentMonth, 2), maxDate) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-slate-500 ' + sizeConfig.navBtnHover}`}
                                aria-label={t('Next Month')}
                            >
                                <IoIosArrowForward />
                            </button>
                        </div>

                        {/* Month Calendars */}
                        <MonthCalendar monthDate={currentMonth} startDate={startDate} endDate={endDate} minDate={minDate} maxDate={maxDate} onDateSelect={handleDateSelect} size={size} />
                        <MonthCalendar monthDate={addMonths(currentMonth, 1)} startDate={startDate} endDate={endDate} maxDate={maxDate} onDateSelect={handleDateSelect} size={size} />
                    </div>

                    <div className="flex justify-center items-center mt-4">
                        <Button theme="primary" size="xxs" className="leading-none" disabled={!startDate || !endDate} onClick={applyDates}>
                            <Icon name="tickMark" size={0.6}></Icon>
                            {t('Apply')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default DateRangePicker
